import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ArrowRight, Users, AlertCircle, Loader2 } from "lucide-react";
import { useShakatahCartStore } from "@/stores/shakatahCartStore";
import { formatCurrency, Currency } from "@/lib/currency";

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function ShakatahCartDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { 
    items, 
    isSyncing,
    updateQuantity, 
    removeItem,
    clearCart,
    syncCart,
  } = useShakatahCartStore();
  
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  
  // Get currency from first item, default to USD
  const currency = (items[0]?.price?.currencyCode as Currency) || 'USD';
  
  // Calculate total price with validation
  const totalPrice = items.reduce((sum, item) => {
    const priceAmount = parseFloat(item.price?.amount || '0');
    return sum + (priceAmount * item.quantity);
  }, 0);
  
  // Check if any items have invalid prices (likely stale data)
  const hasInvalidPrices = items.some(item => {
    const priceAmount = parseFloat(item.price?.amount || '0');
    return isNaN(priceAmount) || priceAmount <= 0;
  });

  // Sync cart when drawer opens to check if checkout was completed
  useEffect(() => {
    if (isOpen) {
      syncCart();
    }
  }, [isOpen, syncCart]);

  const handleProceedToCheckout = () => {
    setIsOpen(false);
    navigate("/shakatah-checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="outline" 
          size="icon" 
          className="relative bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30 hover:border-primary/50"
          title="Shakatah Cart - Split Payment Products"
        >
          <Users className="h-5 w-5 text-primary" />
          {totalItems > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-gradient-to-r from-primary to-primary/70 text-primary-foreground">
              {totalItems}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Split Cart
          </SheetTitle>
          <SheetDescription>
            {totalItems === 0 
              ? "No products for split payment yet" 
              : `${totalItems} item${totalItems !== 1 ? 's' : ''} ready to split with friends`
            }
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No split payment products</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Add Shakatah-enabled products here to split payment with friends
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Scrollable items area */}
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.variantId} className="flex gap-4 p-2 border-b border-primary/20">
                      <div className="w-16 h-16 bg-secondary rounded-md overflow-hidden flex-shrink-0 ring-2 ring-primary/20">
                        {item.product.node.images?.edges?.[0]?.node && (
                          <img
                            src={item.product.node.images.edges[0].node.url}
                            alt={item.product.node.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate text-foreground">{item.product.node.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {item.selectedOptions.map(option => option.value).join(' • ')}
                        </p>
                        <p className="font-semibold text-primary">
                          {formatCurrency(item.price?.amount || 0, currency)}
                        </p>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => removeItem(item.variantId)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                        
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm text-foreground">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Fixed checkout section */}
              <div className="flex-shrink-0 space-y-3 pt-4 border-t border-primary/20 bg-background">
                {hasInvalidPrices && (
                  <div className="flex items-center gap-2 p-2 bg-destructive/10 text-destructive rounded-md text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <span>Some items have invalid prices. </span>
                    <Button variant="link" size="sm" className="p-0 h-auto text-destructive" onClick={clearCart}>
                      Clear cart
                    </Button>
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-foreground">Total</span>
                  <span className="text-xl font-bold text-primary">
                    {formatCurrency(totalPrice, currency)}
                  </span>
                </div>
                
                <Button 
                  onClick={handleProceedToCheckout}
                  className="w-full bg-gradient-to-r from-primary to-primary/70 hover:from-primary/90 hover:to-primary/60" 
                  size="lg"
                  disabled={items.length === 0 || hasInvalidPrices || isSyncing}
                >
                  {isSyncing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Syncing Cart...
                    </>
                  ) : (
                    <>
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Checkout
                    </>
                  )}
                </Button>
                
                <Button 
                  onClick={() => setIsOpen(false)}
                  variant="outline"
                  className="w-full" 
                  size="lg"
                >
                  Add More Products
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
